import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { getAdminCenterData } from '@routes/sidebar/___shared___/model/adminCenterData.slice.ts';
import type { CommonTablePaginatedResponse } from '@routes/sidebar/admin-center/___shared___/types.ts';
import type {
    CountriesRequest,
    CountriesResponse,
    Country,
    Flag,
    FlagsRequest,
    FlagsResponse,
    UpdateCountryRequest,
    UpdateFlagRequest,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/references/api/flags-countries.api.ts';
import {
    getApiCountries,
    getApiFlags,
    updateApiCountry,
    updateApiFlag,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/references/api/flags-countries.api.ts';
import { extractError } from '@utils/slice.ts';

export interface FlagsCountriesError {
    message?: string;
    [k: string]: any;
}

export interface FlagsCountriesState {
    error: FlagsCountriesError | null;

    commonFlags: FlagsResponse | null; // use for common data just in case;
    flags: Flag[];
    flagsPagination: CommonTablePaginatedResponse;
    updatedFlag: Flag | null;
    isGetFlagsPending: boolean;
    isUpdateFlagPending: boolean;

    commonCountries: CountriesResponse | null; // use for common data just in case;
    countries: Country[];
    countriesPagination: CommonTablePaginatedResponse;
    updatedCountry: Country | null;
    currentGetRequestId: string | null;
    isGetCountriesPending: boolean;
    isUpdateCountryPending: boolean;
}

const initialState: FlagsCountriesState = {
    error: null,

    commonFlags: null,
    flags: [],
    flagsPagination: {
        pageNumber: 0,
        pageSize: 0,
        totalPages: 0,
        totalResults: 0,
        maxPageSize: 0,
    },
    updatedFlag: null,
    isGetFlagsPending: false,
    isUpdateFlagPending: false,

    commonCountries: null,
    countries: [],
    countriesPagination: {
        pageNumber: 0,
        pageSize: 0,
        totalPages: 0,
        totalResults: 0,
        maxPageSize: 0,
    },
    updatedCountry: null,
    currentGetRequestId: null,
    isGetCountriesPending: false,
    isUpdateCountryPending: false,
};

export const getFlags = createAsyncThunk<
    FlagsResponse,
    { clientId: number; tenantAlias: string; payload: FlagsRequest },
    { rejectValue: FlagsCountriesError }
>('adminCenterFlagsCountries/getFlags', ({ clientId, tenantAlias, payload }, { rejectWithValue }) =>
    getApiFlags(clientId, tenantAlias, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const updateFlag = createAsyncThunk<
    Flag,
    { clientId: number; tenantAlias: string; id: number; payload: UpdateFlagRequest },
    { rejectValue: FlagsCountriesError }
>('adminCenterFlagsCountries/updateFlag', ({ clientId, tenantAlias, id, payload }, { rejectWithValue }) =>
    updateApiFlag(clientId, tenantAlias, id, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const getCountries = createAsyncThunk<
    CountriesResponse,
    { clientId: number; tenantAlias: string; payload: CountriesRequest },
    { rejectValue: FlagsCountriesError }
>('adminCenterFlagsCountries/getCountries', ({ clientId, tenantAlias, payload }, { rejectWithValue }) =>
    getApiCountries(clientId, tenantAlias, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const updateCountry = createAsyncThunk<
    Country,
    { clientId: number; tenantAlias: string; id: number; payload: UpdateCountryRequest },
    { rejectValue: FlagsCountriesError }
>('adminCenterFlagsCountries/updateCountry', ({ clientId, tenantAlias, id, payload }, { rejectWithValue }) =>
    updateApiCountry(clientId, tenantAlias, id, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

const flagsCountriesSlice = createSlice({
    name: 'adminCenterFlagsCountries',
    initialState,
    reducers: {
        resetFlagsCountriesState: () => initialState,
    },
    extraReducers: (builder) => {
        builder.addCase(getFlags.pending, (state, action) => {
            state.isGetFlagsPending = true;
            state.error = null;
            state.currentGetRequestId = action.meta.requestId;
        });
        builder.addCase(getFlags.fulfilled, (state, action) => {
            if (state.currentGetRequestId !== action.meta.requestId) return;

            state.isGetFlagsPending = false;
            state.currentGetRequestId = null;
            const { data, pagination } = action.payload;
            state.commonFlags = action.payload;
            state.flags = data ?? [];
            state.flagsPagination = pagination;
        });
        builder.addCase(getFlags.rejected, (state, action) => {
            if (state.currentGetRequestId !== action.meta.requestId) return;

            state.isGetFlagsPending = false;
            state.currentGetRequestId = null;
            state.error = extractError(action);
        });

        builder.addCase(updateFlag.pending, (state) => {
            state.isUpdateFlagPending = true;
            state.error = null;
        });
        builder.addCase(updateFlag.fulfilled, (state, action) => {
            state.isUpdateFlagPending = false;
            state.updatedFlag = action.payload;
        });
        builder.addCase(updateFlag.rejected, (state, action) => {
            state.isUpdateFlagPending = false;
            state.error = extractError(action);
        });

        builder.addCase(getCountries.pending, (state, action) => {
            state.isGetCountriesPending = true;
            state.error = null;
            state.currentGetRequestId = action.meta.requestId;
        });
        builder.addCase(getCountries.fulfilled, (state, action) => {
            if (state.currentGetRequestId !== action.meta.requestId) return;

            state.isGetCountriesPending = false;
            state.currentGetRequestId = null;
            const { data, pagination } = action.payload;
            state.commonCountries = action.payload;
            state.countries = data ?? [];
            state.countriesPagination = pagination;
        });
        builder.addCase(getCountries.rejected, (state, action) => {
            if (state.currentGetRequestId !== action.meta.requestId) return;

            state.isGetCountriesPending = false;
            state.currentGetRequestId = null;
            state.error = extractError(action);
        });

        builder.addCase(updateCountry.pending, (state) => {
            state.isUpdateCountryPending = true;
            state.error = null;
        });
        builder.addCase(updateCountry.fulfilled, (state, action) => {
            state.isUpdateCountryPending = false;
            state.updatedCountry = action.payload;
        });
        builder.addCase(updateCountry.rejected, (state, action) => {
            state.isUpdateCountryPending = false;
            state.error = extractError(action);
        });

        builder.addCase(getAdminCenterData.fulfilled, (state, action) => {
            const { flagResponse, countryResponse } = action.payload;

            if (flagResponse) {
                state.commonFlags = flagResponse;
                state.flags = flagResponse.data ?? [];
                state.flagsPagination = flagResponse.pagination;
            }

            if (countryResponse) {
                const { data, pagination } = countryResponse;
                state.commonCountries = countryResponse;
                state.countries = data ?? [];
                state.countriesPagination = pagination;
            }
        });
    },
});

export const { resetFlagsCountriesState } = flagsCountriesSlice.actions;
export default flagsCountriesSlice.reducer;
